import { useTranslation } from 'react-i18next';
import { Alert, Button, Modal, ModalActions, ModalContent, ModalScreenReaderTitle } from '@c2fo/liquidity';
import { CheckCircleIcon } from '@c2fo/liquidity/icons';
import useLocaleFormat from '@/utils/useLocaleFormat';
import { EditError } from './useInvoiceEdits';

interface InvoiceEditLoadingModalProps {
  open: boolean;
  progress?: { current: number; total: number } | null;
  errors?: EditError[];
  onClearErrors?: () => void;
  onClose?: () => void;
}

const InvoiceEditLoadingModal = ({ open, progress, errors, onClearErrors, onClose }: InvoiceEditLoadingModalProps) => {
  const { t } = useTranslation();
  const { asNumber } = useLocaleFormat();

  const hasErrors = errors && errors.length > 0;
  const isProcessing = open && !hasErrors;

  return (
    <Modal className="max-w-[560px]" open={open || hasErrors}>
      <ModalScreenReaderTitle>
        {hasErrors ? t('invoices.updateErrors') : t('invoices.updatingInvoices')}
      </ModalScreenReaderTitle>
      <ModalContent center>
        {isProcessing && (
          <div className="w-full space-y-6 py-6">
            <div className="text-3xl font-medium">{t('invoices.updatingInvoices')}</div>
            {progress && (
              <div className="w-full space-y-4">
                <div>
                  {t('updatingInvoices.processingDivisions', {
                    formattedCount: asNumber({ value: Math.min(progress.current + 1, progress.total) }),
                    formattedTotalCount: asNumber({ value: progress.total }),
                  })}
                </div>
                <div className="relative h-3 w-full animate-pulse overflow-hidden rounded-full">
                  <div className="absolute inset-0 rounded-full bg-gray-200" />
                  <div
                    className="relative h-full bg-primary-500 transition-all duration-300 ease-out"
                    style={{
                      width: `${(Math.min(progress.current + 1, progress.total) / progress.total) * 100}%`,
                    }}
                  />
                </div>
              </div>
            )}
          </div>
        )}
        {hasErrors && (
          <div className="space-y-6 py-6">
            <div className="text-3xl font-medium">{t('updatingInvoices.partialUpdate')}</div>
            {progress ? (
              <div className="flex items-center justify-center gap-2">
                <CheckCircleIcon className="fill-primary-500" />
                {t('updatingInvoices.successfullyUpdated', {
                  formattedCount: asNumber({
                    value: progress.total - errors.reduce((sum, e) => sum + e.takerMarkets.length, 0),
                  }),
                  formattedTotalCount: asNumber({ value: progress.total }),
                })}
              </div>
            ) : null}
            <div className="w-full text-left">
              <Alert
                description={Array.from(
                  new Set(errors.flatMap((error) => error.takerMarkets.map((m) => m.makerOrganizationName)))
                ).join(', ')}
                full
                title={t('updatingInvoices.updatesFailedForTheseCustomers')}
                type="error"
              />
            </div>
            <div>{t('builder.submitErrorDescription3')}</div>
          </div>
        )}
      </ModalContent>
      {hasErrors && (
        <ModalActions>
          <Button
            onClick={() => {
              onClearErrors?.();
              onClose?.();
            }}
            variant="primary"
          >
            {t('core.close')}
          </Button>
        </ModalActions>
      )}
    </Modal>
  );
};

export default InvoiceEditLoadingModal;
